// =============================================================================
// PTEL - Role Duration Types & Utilities
// FB.10.01 · Sistema de roles con expiración
// =============================================================================

// -----------------------------------------------------------------------------
// User Roles
// -----------------------------------------------------------------------------

/** Roles disponibles en PTEL Tool */
export type UserRole = 'admin' | 'moderator' | 'editor' | 'viewer';

/** Etiquetas legibles para cada rol */
export const ROLE_LABELS: Record<UserRole, string> = {
  admin:     'Administrador',
  moderator: 'Moderador',
  editor:    'Editor',
  viewer:    'Visor',
} as const;

// -----------------------------------------------------------------------------
// Duration
// -----------------------------------------------------------------------------

/** Duraciones disponibles para el rol Editor */
export type RoleDuration = '15d' | '3m' | '1y' | 'indefinite';

interface DurationMeta {
  /** Días naturales, null = sin expiración */
  days: number | null;
  /** Etiqueta para UI */
  label: string;
}

/**
 * Mapeo duración → días y etiqueta.
 * Satisfies garantiza exhaustividad sin perder literal types en las claves.
 */
export const ROLE_DURATIONS = {
  '15d':        { days: 15,  label: '15 días' },
  '3m':         { days: 90,  label: '3 meses' },
  '1y':         { days: 365, label: '1 año' },
  'indefinite': { days: null, label: 'Indefinido' },
} as const satisfies Record<RoleDuration, DurationMeta>;

// -----------------------------------------------------------------------------
// Expiration Calculator
// -----------------------------------------------------------------------------

/**
 * Calcula la fecha de expiración a partir de una duración.
 * @param duration - Clave de duración ('15d', '3m', '1y', 'indefinite')
 * @param from - Fecha base (por defecto: ahora)
 * @returns Date de expiración, o null si es indefinido
 */
export function calculateExpirationDate(
  duration: RoleDuration,
  from: Date = new Date(),
): Date | null {
  const { days } = ROLE_DURATIONS[duration];
  if (days === null) return null;

  const expiration = new Date(from.getTime());
  expiration.setDate(expiration.getDate() + days);
  return expiration;
}

// -----------------------------------------------------------------------------
// Editor Request
// -----------------------------------------------------------------------------

export type EditorRequestStatus = 'pending' | 'approved' | 'rejected';

export interface EditorRequest {
  /** ID del documento Firestore (asignado por Firestore) */
  id?: string;
  /** UID del solicitante */
  uid: string;
  /** Email del solicitante */
  email: string;
  /** Nombre visible del solicitante */
  displayName: string;
  /** Justificación libre (máx. 500 caracteres) */
  reason: string;
  /** Fecha de solicitud */
  requestedAt: Date;
  /** Estado de la solicitud */
  status: EditorRequestStatus;
  /** UID del admin que revisó */
  reviewedBy?: string;
  /** Fecha de revisión */
  reviewedAt?: Date;
  /** Duración concedida (solo si aprobada) */
  grantedDuration?: RoleDuration;
  /** Motivo del rechazo (solo si rechazada) */
  rejectionReason?: string;
}
